/**
 * persistence.ts
 * フロー（ノード・エッジ）の localStorage 保存と復元
 */
import type { AppEdge, AppNode, NodeKind } from './types';
import { isNodeKind } from './nodeRegistry';
import { isFixedNodeId } from './config/fixedNodes';
import { SWITCH_SOURCE_HANDLES } from './nodes/switchHandles';
import { normalizeWalkingData } from './config/walkingMotions';

export const getFlowStorageKey = (userSub: string) => `filo:flow:v1:${userSub}`;
export const getFunctionStorageKey = (userSub: string, functionName: string) =>
  `filo:function:v1:${userSub}:${encodeURIComponent(functionName.trim())}`;

/** 旧名称で保存されたフローを現行のノード種別・ハンドルIDへ読み替える */
const LEGACY_NODE_TYPES: Record<string, NodeKind> = {
  move: 'gesture',
  switchBox: 'switch',
  out: 'output',
};

const LEGACY_SOURCE_HANDLES: Record<string, string> = {
  'switch-box-match-1': SWITCH_SOURCE_HANDLES.match1,
  'switch-box-match-2': SWITCH_SOURCE_HANDLES.match2,
  'switch-box-default': SWITCH_SOURCE_HANDLES.default,
};

const migrateNodeType = (node: AppNode): AppNode => {
  const renamed = node.type ? LEGACY_NODE_TYPES[node.type] : undefined;
  const migrated = renamed
    ? ({ ...node, id: node.id === 'out' ? 'output' : node.id, type: renamed } as AppNode)
    : node;
  if (migrated.type === 'walking') return { ...migrated, data: normalizeWalkingData(migrated.data) };
  if (migrated.type !== 'function') return migrated;
  return {
    ...migrated,
    data: {
      ...migrated.data,
      internalNodes: migrated.data.internalNodes.map(migrateNodeType),
      internalEdges: migrated.data.internalEdges.map(migrateEdge),
    },
  };
};

const migrateEdge = (edge: AppEdge): AppEdge => {
  const renamed = edge.sourceHandle ? LEGACY_SOURCE_HANDLES[edge.sourceHandle] : undefined;
  return {
    ...edge,
    source: edge.source === 'out' ? 'output' : edge.source,
    target: edge.target === 'out' ? 'output' : edge.target,
    sourceHandle: renamed ?? edge.sourceHandle,
  };
};

type PersistedFlow = {
  version: 1;
  savedAt: string;
  nodes: AppNode[];
  edges: AppEdge[];
};

type PersistedFunction = {
  version: 1;
  savedAt: string;
  name: string;
  nodes: AppNode[];
  edges: AppEdge[];
};

const stripEdgeRuntimeProps = (edge: AppEdge): AppEdge => ({
  ...edge,
  selected: undefined,
  className: undefined,
});

const stripNodeRuntimeProps = (node: AppNode): AppNode => {
  const stripped = {
    ...node,
    selected: undefined,
    dragging: undefined,
    className: undefined,
  } as AppNode;
  if (stripped.type !== 'function') return stripped;
  return {
    ...stripped,
    data: {
      ...stripped.data,
      internalNodes: stripped.data.internalNodes.map(stripNodeRuntimeProps),
      internalEdges: stripped.data.internalEdges.map(stripEdgeRuntimeProps),
    },
  };
};

export type FlowSaveResult =
  | { success: true; savedAt: string }
  | { success: false; error: string };

/** 実行時専用のプロパティ（選択状態・ハイライト）を落として保存する */
export const saveFlow = (userSub: string, nodes: AppNode[], edges: AppEdge[]): FlowSaveResult => {
  const savedAt = new Date().toISOString();
  const payload: PersistedFlow = {
    version: 1,
    savedAt,
    nodes: nodes.map(stripNodeRuntimeProps),
    edges: edges.map(stripEdgeRuntimeProps),
  };

  try {
    localStorage.setItem(getFlowStorageKey(userSub), JSON.stringify(payload));
    return { success: true, savedAt };
  } catch (error) {
    const message = error && typeof error === 'object' && 'message' in error
      && typeof error.message === 'string'
      ? error.message
      : 'ブラウザへ保存できませんでした';
    return {
      success: false,
      error: message,
    };
  }
};

/** 復元。壊れている・不正な場合は null（呼び出し側で初期状態にフォールバック） */
export const loadFlow = (userSub: string): { nodes: AppNode[]; edges: AppEdge[] } | null => {
  try {
    const raw = localStorage.getItem(getFlowStorageKey(userSub));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PersistedFlow;
    if (parsed.version !== 1 || !Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
      return null;
    }

    const nodes = parsed.nodes
      .filter((node) => node && typeof node.id === 'string')
      .map(migrateNodeType)
      .map(stripNodeRuntimeProps)
      .filter((node) => isNodeKind(node.type));
    const hasStart = nodes.some((node) => node.id === 'start');
    const hasEnd = nodes.some((node) => node.id === 'end');
    if (!hasStart || !hasEnd) {
      return null;
    }

    const ids = new Set(nodes.map((node) => node.id));
    const edges = parsed.edges
      .map(migrateEdge)
      .map(stripEdgeRuntimeProps)
      .filter((edge) => edge && ids.has(edge.source) && ids.has(edge.target))
    const restoredNodes = nodes.map((node) =>
      isFixedNodeId(node.id)
        // 旧版は draggable: false で保存されているため、undefined で打ち消して全体設定に従わせる
        ? ({ ...node, draggable: undefined, deletable: false } as AppNode)
        : node
    );

    return { nodes: restoredNodes, edges };
  } catch {
    return null;
  }
};

export const clearSavedFlow = (userSub: string): void => {
  try {
    localStorage.removeItem(getFlowStorageKey(userSub));
  } catch {
    // no-op
  }
};

export const saveFunction = (
  userSub: string,
  functionName: string,
  nodes: AppNode[],
  edges: AppEdge[],
): void => {
  const name = functionName.trim();
  if (!name) return;
  const payload: PersistedFunction = {
    version: 1,
    savedAt: new Date().toISOString(),
    name,
    nodes: nodes.map(stripNodeRuntimeProps),
    edges: edges.map(stripEdgeRuntimeProps),
  };
  try {
    localStorage.setItem(getFunctionStorageKey(userSub, name), JSON.stringify(payload));
  } catch {
    // 容量超過などはカード内の保存だけを継続する
  }
};

export const loadFunction = (
  userSub: string,
  functionName: string,
): { nodes: AppNode[]; edges: AppEdge[] } | null => {
  const name = functionName.trim();
  if (!name) return null;
  try {
    const raw = localStorage.getItem(getFunctionStorageKey(userSub, name));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedFunction;
    if (parsed.version !== 1 || !Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
      return null;
    }
    const nodes = parsed.nodes
      .filter((node) => node && typeof node.id === 'string')
      .map(migrateNodeType)
      .map(stripNodeRuntimeProps)
      .filter((node) => isNodeKind(node.type));
    if (!nodes.some((node) => node.id === 'input') || !nodes.some((node) => node.id === 'output')) {
      return null;
    }
    const ids = new Set(nodes.map((node) => node.id));
    return {
      nodes: nodes.map((node) => isFixedNodeId(node.id) ? { ...node, deletable: false } as AppNode : node),
      edges: parsed.edges
        .map(migrateEdge)
        .map(stripEdgeRuntimeProps)
        .filter((edge) => edge && ids.has(edge.source) && ids.has(edge.target)),
    };
  } catch {
    return null;
  }
};
